// Regional UFO sighting guides: what gets reported where, the notable local incidents,
// and what usually explains them. Shared by the React place screens and the edge renderer
// (seo/render.ts), so crawlers get the same copy users read.

export interface PlaceIncident {
  year: number;
  name: string;
  body: string;
  /** Case file link, when the archive has one. */
  href?: string;
}

export interface Place {
  slug: string;
  /** Display name, e.g. "the United Kingdom". */
  name: string;
  /** Short region label for cards and breadcrumbs. */
  region: string;
  /** Approximate centre of the region — the coordinate Tonight's Sky is computed for. */
  latitude: number;
  longitude: number;
  /** H1 without the site name. */
  title: string;
  description: string;
  intro: string[];
  /** What people tend to report here, and what usually explains it. */
  reported: string[];
  incidents: PlaceIncident[];
  faqs: Array<{ question: string; answer: string }>;
}

export const PLACES_PATH = '/ufo-sightings';
export const PLACES_TITLE = 'UFO Sightings by Place: Regional Guides and Case Files';
export const PLACES_DESCRIPTION =
  'Where UFO sightings happen and what usually explains them: regional guides with notable local incidents, common explanations and the case files to read.';
export const PLACES_UPDATED = '2026-10-30';

export const PLACES: Place[] = [
  // ---------------------------------------------------------------- United Kingdom
  {
    slug: 'united-kingdom',
    name: 'the United Kingdom',
    region: 'Europe',
    latitude: 54.0,
    longitude: -2.0,
    title: 'UFO Sightings in the United Kingdom',
    description:
      'UK UFO sightings: Rendlesham Forest, the Calvine photograph and the Broad Haven wave, plus what the MoD’s released files say about British reports.',
    intro: [
      'The United Kingdom has one of the best-documented reporting histories anywhere, because the Ministry of Defence kept a UFO desk from the 1950s until 2009 and released more than 12,000 of its files through the National Archives. That record covers everything from radar traces over airbases to farmers’ evenings ruined by satellites.',
      'British reports cluster around two things: military airspace, where trained observers and sensors are watching, and dark rural skies, where satellites, planets and aircraft on approach paths look their strangest. Scotland, Wales and East Anglia between them hold most of the famous cases.',
    ],
    reported: [
      'Lights over airbases and the nuclear submarine facilities on the Clyde — the MOD files are full of them, usually resolved as aircraft or exercises.',
      'Satellite trains over the north, where dark skies make Starlink batches visible across whole villages.',
      'Triangle formations, most often aircraft lined up for approach at a regional airport.',
      'Orange drifting lights on summer weekends: sky lanterns released at weddings and festivals.',
    ],
    incidents: [
      {
        year: 1980,
        name: 'The Rendlesham Forest incident',
        body:
          'Three nights in late December 1980, US Air Force personnel from RAF Woodbridge reported a glowing object in the Suffolk forest, with radiation readings recorded afterwards. Deputy base commander Lt. Col. Charles Halt’s signed memorandum is one of the best primary documents in the field.',
        href: '/case/TU-1980-000001',
      },
      {
        year: 1990,
        name: 'The Calvine photograph',
        body:
          'Two hikers near Calvine, Perthshire, photographed a large diamond-shaped object above the valley with a jet passing beneath it. The negatives were held by the MOD and later lost; a print resurfaced in 2022.',
        href: '/case/TU-1990-000001',
      },
      {
        year: 1977,
        name: 'The Broad Haven wave',
        body:
          'Over several months in Pembrokeshire, schoolchildren and adults reported a silver object and strange lights near the village. The “Welsh triangle” reports were investigated by the MOD’s desk and remain in the released files.',
        href: '/case/TU-1977-000001',
      },
      {
        year: 1993,
        name: 'The Cosford and Shawbury incident',
        body:
          'On the night of 30–31 March 1993, reports of a huge triangular object with silent, pulsing lights swept from Wales across the Midlands, with sightings logged at two RAF stations. The MOD’s closing note called it “the most important sighting reported to the Ministry in more than 30 years”.',
      },
    ],
    faqs: [
      {
        question: 'Does the UK government still take UFO reports?',
        answer:
          'No. The Ministry of Defence closed its UFO desk in 2009, judging the few genuinely interesting cases beyond its remit. There is no official British reporting channel; UK sightings go to civilian databases, and anything that poses a safety risk should go to the police or air traffic control first.',
      },
      {
        question: 'Where in the UK are most UFOs reported?',
        answer:
          'Reports follow population and dark skies: the south of England and the Midlands produce the most by volume, while Scotland and Wales produce some of the best-documented cases thanks to dark skies and military airspace. Most UK reports resolve to satellites, aircraft and lanterns.',
      },
    ],
  },
  // ---------------------------------------------------------------- England
  {
    slug: 'england',
    name: 'England',
    region: 'United Kingdom',
    latitude: 52.36,
    longitude: -1.47,
    title: 'UFO Sightings in England',
    description:
      'English UFO sightings: Rendlesham Forest in Suffolk, the Cosford incident, the O’Hare-style airport reports, and what the Ministry of Defence files show.',
    intro: [
      'England’s UFO history runs from the 1950s waves, when radar operators and pilots logged object after object, to the Rendlesham Forest incident that gave the country its own Roswell. The Ministry of Defence’s released files hold more than a decade’s worth of English reports, most of them resolved within days.',
      'The pattern in English reports is consistent: lights near airbases and approach paths, satellite trains over dark countryside, and the occasional genuinely odd report that stays in the files unexplained for want of data.',
    ],
    reported: [
      'Objects over and around RAF and US Air Force bases in East Anglia, the Midlands and the west country — a lot of traffic, a lot of observers, a lot of reports.',
      'Bright steady lights crossing the sky at dusk: satellites, and most often Starlink trains in the weeks after a launch.',
      'V-shaped formations over motorways at night, usually several aircraft on the same approach path.',
      'Orange lanterns drifting across summer skies, and fireballs mistaken for something closer.',
    ],
    incidents: [
      {
        year: 1980,
        name: 'Rendlesham Forest, Suffolk',
        body:
          'The best-known British case: three nights of reports from US personnel at RAF Woodbridge, a signed officer’s memorandum, and radiation readings at the site. The MOD closed it with no defence significance.',
        href: '/case/TU-1980-000001',
      },
      {
        year: 1993,
        name: 'Cosford and Shawbury, Shropshire',
        body:
          'A huge silent triangle with pulsing lights reported across the Midlands in March 1993, including by personnel at two RAF stations. The MOD took it seriously enough to keep it on file for decades.',
      },
      {
        year: 2026,
        name: 'Recent witness reports',
        body:
          'England still produces steady reports: the archive holds recent submissions from Kingston upon Hull and elsewhere, each checked automatically against satellite passes, astronomy and weather.',
        href: '/case/TU-2026-000001',
      },
    ],
    faqs: [
      {
        question: 'What was the Rendlesham Forest incident?',
        answer:
          'In late December 1980, US Air Force personnel near RAF Woodbridge in Suffolk reported a glowing object moving through Rendlesham Forest over three nights. Radioactivity was recorded at the site, and Deputy Base Commander Lt. Col. Charles Halt signed a memorandum documenting it in January 1981. It remains unexplained in the MOD’s released files.',
      },
      {
        question: 'Are there many UFO reports in England today?',
        answer:
          'Yes — several thousand a year nationally at peak, though the MOD no longer counts them. Most resolve to satellites, aircraft, drones and lanterns. The sky checker on this site runs the same checks investigators use.',
      },
    ],
  },
  // ---------------------------------------------------------------- Scotland
  {
    slug: 'scotland',
    name: 'Scotland',
    region: 'United Kingdom',
    latitude: 56.8,
    longitude: -4.2,
    title: 'UFO Sightings in Scotland',
    description:
      'Scottish UFO sightings: the Calvine photograph, the Bonnybridge reports, dark-sky satellite watching and what explains most Highland sightings.',
    intro: [
      'Scotland punches above its weight in UFO history, largely because of its dark skies, its military ranges and its long coastline. The country’s best-known image is the Calvine photograph: a diamond-shaped object photographed above a Highland glen in 1990, with a jet passing beneath it for scale.',
      'Bonnybridge, in the central lowlands, became synonymous with reports in the 1990s, and the Highlands and Islands still produce the clearest satellite and ISS passes in Britain.',
    ],
    reported: [
      'Triangle and diamond-shaped objects over the Highlands — the classic Scottish report shape, of which Calvine is the famous example.',
      'Lights over the Faslane and Coulport submarine bases on the Clyde, common in the MOD files and usually aircraft.',
      'Bonnybridge-area lights: a persistent stream of reports through the 1990s and 2000s from the Falkirk area.',
      'Satellite trains and ISS passes, best over the Highlands where light pollution is lowest.',
    ],
    incidents: [
      {
        year: 1990,
        name: 'The Calvine photograph',
        body:
          'In August 1990, two hikers above Calvine in Perthshire photographed a large diamond-shaped object hovering in the glen, with a military jet visible beneath it. The negatives passed to the MOD and were later lost; the best-known print was released in 2022.',
        href: '/case/TU-1990-000001',
      },
      {
        year: 1996,
        name: 'The Bonnybridge reports',
        body:
          'The Falkirk village of Bonnybridge became Britain’s unofficial UFO capital after a sustained stream of light reports through the 1990s. Local councillor Billy Buchanan campaigned for an official inquiry; the MOD declined, and most investigated reports resolved to aircraft.',
      },
    ],
    faqs: [
      {
        question: 'What is the Calvine photograph?',
        answer:
          'A photograph taken near Calvine, Perthshire, in August 1990 showing a large diamond-shaped object above a Highland valley with a jet passing underneath. The negatives were held by the Ministry of Defence and later lost. It is one of the most analysed UFO images in the world, and opinions on it remain divided.',
      },
      {
        question: 'Why are there so many reports from the Highlands?',
        answer:
          'Dark skies, low light pollution and a lot of open sky. Satellites, the ISS and meteors are visible from the Highlands far more often than from central Scotland, which accounts for much of the volume.',
      },
    ],
  },
  // ---------------------------------------------------------------- Wales
  {
    slug: 'wales',
    name: 'Wales',
    region: 'United Kingdom',
    latitude: 52.2,
    longitude: -3.7,
    title: 'UFO Sightings in Wales',
    description:
      'Welsh UFO sightings: the Broad Haven landing reports of 1977, the “Welsh triangle”, and what the Ministry of Defence files hold.',
    intro: [
      'Wales has one of the strangest chapters in British UFO history: the Broad Haven reports of 1977, in which schoolchildren and adults in Pembrokeshire described a silver object and lights over several months. The Ministry of Defence investigated and filed it away.',
      'Today, Welsh reports follow the national pattern — dark coastlines and national parks produce satellite and fireball reports, while the M4 corridor produces aircraft ones.',
    ],
    reported: [
      'The 1977 Pembrokeshire wave: silver objects, strange lights and a reported landing mark, gathered around one village school.',
      'Coastal lights: fishing boats, flares, and the occasional exercise over the Milford Haven waterway.',
      'Satellite trains over Snowdonia and the Brecon Beacons, where skies are darkest.',
      'Drone activity over industrial sites in the south, increasingly common in reports since 2018.',
    ],
    incidents: [
      {
        year: 1977,
        name: 'The Broad Haven wave',
        body:
          'Children at Broad Haven primary school drew the same silver object repeatedly, and adults across Pembrokeshire reported lights over several months. The MOD’s UFO desk investigated, found no defence significance, and closed it — the files were released in 2008.',
        href: '/case/TU-1977-000001',
      },
    ],
    faqs: [
      {
        question: 'What was the “Welsh triangle”?',
        answer:
          'The name given to the cluster of 1977 reports around Broad Haven in Pembrokeshire: schoolchildren, farmers and residents describing a silver object and lights, with a few reported landing traces. The files are public through the National Archives and remain unexplained for want of data.',
      },
      {
        question: 'Is Wales a good place to watch satellites?',
        answer:
          'Yes. Snowdonia, the Brecon Beacons and the Pembrokeshire coast have some of the darkest skies in Britain, which makes Starlink trains, the ISS and meteors easy to see with the naked eye.',
      },
    ],
  },
  // ---------------------------------------------------------------- United States
  {
    slug: 'united-states',
    name: 'the United States',
    region: 'North America',
    latitude: 39.83,
    longitude: -98.58,
    title: 'UFO Sightings in the United States',
    description:
      'US UFO sightings: Roswell, the Phoenix Lights, O’Hare, Stephenville and the Washington radar wave — plus what explains most American reports today.',
    intro: [
      'The modern UFO era is American. Kenneth Arnold’s 1947 sighting gave the phenomenon its name, Roswell gave it its myth, and Project Blue Book gave it 22 years of government paperwork. Today the United States produces more reports than any other country, helped by dense air traffic, huge military ranges and a population that mostly isn’t shy about reporting.',
      'American reports have a distinctive flavour: military pilots and air traffic controllers as witnesses, radar alongside eyes, and a steady background of lights near bases. The last few years have added Senate hearings and a permanent Pentagon office to the picture.',
    ],
    reported: [
      'Lights near military installations — the country’s biggest single category, driven by training traffic and alert observers.',
      'Bright lights on approach paths to major airports, usually aircraft with landing lights on.',
      'Desert southwest orbs at dusk: satellites, lanterns and the occasional research balloon.',
      'Fireball meteors reported by hundreds at once, especially during the Perseids and Geminids.',
    ],
    incidents: [
      {
        year: 1947,
        name: 'Roswell, New Mexico',
        body:
          'A ranch debris find, a retracted “flying disc” press release, and seventy years of argument. Official reviews attribute the debris to the classified Project Mogul balloon programme; it remains the most famous UFO story ever told.',
        href: '/case/TU-1947-000001',
      },
      {
        year: 1952,
        name: 'The Washington radar wave',
        body:
          'Unidentified returns tracked by radar over Washington DC on consecutive July weekends in 1952, with fighters scrambled, produced the biggest press conference since the war. The air force blamed temperature inversion.',
      },
      {
        year: 1997,
        name: 'The Phoenix Lights',
        body:
          'A slow-moving V-formation watched by thousands across Arizona, followed hours later by flares over the Goldwater Range. The two events are usually treated separately.',
        href: '/case/TU-1997-000001',
      },
      {
        year: 2006,
        name: 'O’Hare Airport, Chicago',
        body:
          'United employees and pilots reported a metallic disc hovering over gate C17 before it shot upward. The FAA called it weather.',
        href: '/case/TU-2006-000001',
      },
      {
        year: 2008,
        name: 'Stephenville, Texas',
        body:
          'Dozens of residents reported large lights over Erath County; radar revisions showed unknown tracks, and the air force later acknowledged F-16s flying nearby.',
        href: '/case/TU-2008-000001',
      },
    ],
    faqs: [
      {
        question: 'Which US state has the most UFO reports?',
        answer:
          'By raw volume, the most populous states lead: California, Texas, Florida, Pennsylvania and New York. Per head of population, darker and more rural states such as Montana, Vermont and Alaska rate higher. Volume tracks people and night skies, not mystery.',
      },
      {
        question: 'Can I report a UFO to the US government?',
        answer:
          'Most members of the public cannot file with AARO, the Pentagon’s UAP office; its reporting channels are for current and former government employees, service members and contractors. Civilian databases such as NUFORC and this site take reports from anyone.',
      },
    ],
  },
  // ---------------------------------------------------------------- New Mexico
  {
    slug: 'new-mexico',
    name: 'New Mexico',
    region: 'United States',
    latitude: 34.4,
    longitude: -106.1,
    title: 'UFO Sightings in New Mexico',
    description:
      'New Mexico UFO sightings: Roswell, the Socorro landing of 1964, the green fireballs over Los Alamos, and why the state leads American UFO history.',
    intro: [
      'No American state is more tangled up with UFO history than New Mexico. In 1947 it gave the world Roswell; in 1964 it produced the Lonnie Zamora landing report at Socorro, the case that converted J. Allen Hynek from sceptic to investigator; and through the late 1940s its skies over Los Alamos filled with “green fireballs” that the air force took seriously enough to study formally.',
      'There was a practical reason: in 1947 New Mexico held the country’s nuclear weapons complex and its most secret aerospace work. A lot of people were watching the sky for good reasons, and a lot of what they saw was theirs.',
    ],
    reported: [
      'Orange orbs drifting over the desert at dusk — almost always sky lanterns or, before 1947, atmospheric phenomena.',
      'Lights near Kirtland, Holloman and Cannon air force bases, and along the White Sands missile range.',
      'The annual Roswell festival season in July, when reports spike with visitors and publicity.',
      'Fireballs over the desert, spectacularly visible in the clear air and reported statewide.',
    ],
    incidents: [
      {
        year: 1947,
        name: 'Roswell',
        body:
          'The Roswell Army Air Field’s “flying disc” press release of 8 July 1947 and its retraction a day later. Official reviews attribute the debris to Project Mogul, a classified balloon programme.',
        href: '/case/TU-1947-000001',
      },
      {
        year: 1948,
        name: 'The green fireballs',
        body:
          'From late 1948, green fireballs crossed the skies over Los Alamos and the nuclear installations often enough that the air force opened Project Twinkle to instrument them. They remain a genuinely open question in the files.',
      },
      {
        year: 1964,
        name: 'The Socorro landing',
        body:
          'Police officer Lonnie Zamora chased a sound over the desert outside Socorro and saw an egg-shaped object with two figures beside it rise on a blue-white flame and depart. Physical traces — burnt brush and landing gear marks — were documented at the site.',
      },
    ],
    faqs: [
      {
        question: 'What actually happened at Roswell?',
        answer:
          'In July 1947 the Roswell Army Air Field announced it had recovered a “flying disc”, then said the debris was a weather balloon. Later official reviews identified it as Project Mogul, a classified high-altitude balloon programme. No evidence of extraterrestrial material has ever been produced, and the case’s cultural life has far outgrown the paperwork.',
      },
      {
        question: 'Do people still report UFOs in New Mexico?',
        answer:
          'Yes — the state produces steady reports for its population, concentrated around the air bases, White Sands and the dark desert skies. Most resolve to satellites, aircraft and lanterns, like everywhere else.',
      },
    ],
  },
  // ---------------------------------------------------------------- Arizona
  {
    slug: 'arizona',
    name: 'Arizona',
    region: 'United States',
    latitude: 34.2,
    longitude: -111.6,
    title: 'UFO Sightings in Arizona',
    description:
      'Arizona UFO sightings: the Phoenix Lights of March 1997, V-formations over the desert, and what explains most reports in the state today.',
    intro: [
      'Arizona’s place in UFO history was sealed on the evening of 13 March 1997, when a slow-moving V-formation of lights crossed the state and thousands watched it — including, as it turned out, the governor. The Phoenix Lights are still the most-witnessed mass sighting in modern American history.',
      'The state’s dark desert skies, its military ranges and its winter population of skywatchers keep the reports coming. Most today resolve to satellites, aircraft and the flares that explained part of 1997’s show.',
    ],
    reported: [
      'V-formations of lights moving slowly across the desert — the report shape Phoenix made famous.',
      'Flares over the Barry M. Goldwater Range in the southwest, visible from Phoenix and Tucson on training nights.',
      'Orange orbs at dusk, drifting with the wind: sky lanterns and, occasionally, Chinese lanterns released at resort events.',
      'ISS and satellite passes, exceptionally clear from the Sonoran Desert.',
    ],
    incidents: [
      {
        year: 1997,
        name: 'The Phoenix Lights',
        body:
          'A V-formation of lights crossed Nevada, Arizona and Sonora between about 19:30 and 22:30 local time, watched by thousands. Flares dropped by Maryland Air National Guard A-10s explain the later lights; the earlier formation remains disputed.',
        href: '/case/TU-1997-000001',
      },
    ],
    faqs: [
      {
        question: 'Have the Phoenix Lights been explained?',
        answer:
          'Partly. The second group of lights, seen around 22:00, was flares dropped by A-10s training over the Goldwater Range, confirmed by the Air National Guard. The earlier slow-moving formation, seen from across the state at 19:30, has no official explanation; a formation of aircraft is the usual proposed one.',
      },
      {
        question: 'Where is the best place to watch the sky in Arizona?',
        answer:
          'Away from Phoenix and Tucson: the Sonoran Desert, Sedona’s outskirts, the north rim of the Grand Canyon and the Organ Pipe area all have dark skies. The state parks run regular star parties.',
      },
    ],
  },
  // ---------------------------------------------------------------- Texas
  {
    slug: 'texas',
    name: 'Texas',
    region: 'United States',
    latitude: 31.4,
    longitude: -99.3,
    title: 'UFO Sightings in Texas',
    description:
      'Texas UFO sightings: the Stephenville lights of 2008, the Lubbock lights of 1951, the Cash-Landrum case and what explains most reports today.',
    intro: [
      'Texas is a big state with big skies, military bases at almost every corner, and a reporting tradition that runs from the 1951 Lubbock Lights through Stephenville in 2008. The state’s cases tend to share a pattern: many civilian witnesses, some radar, and an air force that admits being nearby without quite explaining why.',
      'Today, most Texas reports come from the big metropolitan corridors, where aircraft, drones and satellites account for the vast majority once checked.',
    ],
    reported: [
      'Quiet V-formations and lines of lights over rural counties at dusk: aircraft, or in rare cases something better documented.',
      'Lights near Dyess, Sheppard, Laughlin and the other west Texas bases.',
      'Desert orbs in the Big Bend and Permian Basin, usually satellites.',
      'Fireballs visible across hundreds of miles in the flat, clear visibility.',
    ],
    incidents: [
      {
        year: 1951,
        name: 'The Lubbock Lights',
        body:
          'In August and September 1951, groups of low blue-white lights crossed Lubbock in loose V-formations, photographed by a Texas Tech student and reported by professors. Project Blue Book investigated; the photographs were never convincingly explained.',
      },
      {
        year: 1980,
        name: 'The Cash-Landrum incident',
        body:
          'On a December night near Huffman, two men and a child reported a diamond-shaped object escorted by helicopters, and later suffered symptoms consistent with radiation exposure. It is one of the few US cases with documented medical aftermath, and no official explanation.',
      },
      {
        year: 2008,
        name: 'The Stephenville lights',
        body:
          'Dozens of Erath County residents reported large silent lights in early January 2008. AP obtained FAA radar revisions showing unknown tracks, and the air force later acknowledged F-16s had been flying in the area.',
        href: '/case/TU-2008-000001',
      },
    ],
    faqs: [
      {
        question: 'What happened at Stephenville in 2008?',
        answer:
          'In the first week of January 2008, residents across Erath County reported large silent lights and a huge object. The FAA released revised radar data showing unknown tracks in the area, and the air force confirmed F-16s had been flying nearby — without fully explaining the activity. The archive has a full case file.',
      },
      {
        question: 'Are the Lubbock Lights still unexplained?',
        answer:
          'Officially, Project Blue Book never identified the lights in the photographs. A flock of birds is the most cited conventional explanation for the formation, though the photographers disputed it. The case remains open in popular history and closed in the archives.',
      },
    ],
  },
  // ---------------------------------------------------------------- Illinois
  {
    slug: 'illinois',
    name: 'Illinois',
    region: 'United States',
    latitude: 40.0,
    longitude: -89.2,
    title: 'UFO Sightings in Illinois',
    description:
      'Illinois UFO sightings: the O’Hare Airport disc of 2006, Chicago’s aviation reports, and what explains most sightings in the state.',
    intro: [
      'Illinois has one of the most credible single reports in the modern archive: the 2006 O’Hare case, in which United Airlines employees and pilots watched a metallic disc hover over gate C17 and then shoot upward through the cloud layer. Trained aviation witnesses, multiple angles, one airport — the case is a favourite of investigators on every side.',
      'Outside Chicago, Illinois reports are mostly rural: satellite trains over farm country, fireballs, and the occasional aircraft on approach to one of the state’s smaller airports.',
    ],
    reported: [
      'Disc and orb reports over O’Hare and Midway, usually aircraft on approach — but 2006’s report resisted that explanation.',
      'Satellite trains over central Illinois, visible across the flat farmland.',
      'Orange lights drifting on autumn evenings: sky lanterns at festivals and fundraisers.',
      'Meteor reports during the major showers, boosted by Chicago’s population.',
    ],
    incidents: [
      {
        year: 2006,
        name: 'The O’Hare disc',
        body:
          'On the evening of 7 November 2006, a dozen or more United employees and pilots reported a smooth metallic disc hovering over gate C17 before it accelerated vertically, leaving a hole in the overcast. The FAA attributed it to a weather phenomenon; the Chicago Tribune broke the story in 2007.',
        href: '/case/TU-2006-000001',
      },
    ],
    faqs: [
      {
        question: 'Was the O’Hare UFO ever identified?',
        answer:
          'No. The FAA attributed the report to a weather phenomenon, and United initially said no employees would comment. FOIA requests later confirmed that multiple pilots and ground staff filed reports. The case stays unexplained, though aircraft, birds and weather remain on the table.',
      },
      {
        question: 'Do O’Hare pilots report UFOs often?',
        answer:
          'Pilots report more than the public hears about: reporting one carries career risk, which is why the 2006 case being logged by so many United staff was unusual. Organisations like Americans for Safe Aerospace now exist specifically to give pilots a safe channel.',
      },
    ],
  },
  // ---------------------------------------------------------------- New York
  {
    slug: 'new-york',
    name: 'New York',
    region: 'United States',
    latitude: 42.9,
    longitude: -75.5,
    title: 'UFO Sightings in New York',
    description:
      'New York UFO sightings: the Hudson Valley boomerang wave of the 1980s, the state’s Project Blue Book files and what explains most reports today.',
    intro: [
      'New York’s signature UFO event was not a single night but a wave: through the mid-1980s, thousands of people across the Hudson Valley reported a enormous dark boomerang or triangle moving silently overhead. The Dutchess County sightings became national news and are still argued over.',
      'Today the state’s reports concentrate along the Hudson corridor and Long Island, where air traffic is dense and satellite passes stand out against the light pollution from New Jersey to Connecticut.',
    ],
    reported: [
      'Silent boomerang and triangle formations, the shape the Hudson Valley wave made famous.',
      'Lights over Long Island’s approach paths: aircraft stacking for JFK and LaGuardia.',
      'Fireballs over the Adirondacks, where skies are genuinely dark.',
      'Drone clusters over the Hudson and the Catskills, increasingly common in reports.',
    ],
    incidents: [
      {
        year: 1983,
        name: 'The Hudson Valley boomerang',
        body:
          'From 1983 through 1986, thousands reported a vast silent V-shaped object crossing the Hudson Valley, often at low altitude. The Air Force investigated quietly; explanations offered include ultralight aircraft flying in formation, which witnesses dispute on size and scale.',
      },
    ],
    faqs: [
      {
        question: 'What was the Hudson Valley UFO?',
        answer:
          'A wave of reports from 1983–86 of a large boomerang-shaped object moving silently over Dutchess and Putnam counties. Mass witnesses, including police officers, described something far larger than any aircraft. Ultralight planes flying in formation are the leading conventional explanation, and the case remains disputed.',
      },
      {
        question: 'Are there still UFO reports from New York?',
        answer:
          'Steady ones, mostly from outside the city where skies are darker. Like everywhere else, most resolve to satellites, aircraft and drones once the time and place are checked.',
      },
    ],
  },
  // ---------------------------------------------------------------- California
  {
    slug: 'california',
    name: 'California',
    region: 'United States',
    latitude: 37.2,
    longitude: -119.5,
    title: 'UFO Sightings in California',
    description:
      'California UFO sightings: the Battle of Los Angeles in 1942, the Nimitz Tic Tac off San Diego, and what explains most reports in the state.',
    intro: [
      'California has the densest UFO reporting in the country, for the obvious reasons: 40 million people, the biggest military aviation complex on Earth, and coastlines where carrier groups train within sight of cities. It also holds two of the most iconic American events — the anti-aircraft barrage over Los Angeles in 1942, and the Nimitz encounter off San Diego in 2004.',
      'The state’s reports split cleanly between Southern California’s urban skies, where aircraft and satellites dominate, and the deserts and sierra, where dark skies produce the clearer footage.',
    ],
    reported: [
      'Lights along the coast from launch and carrier traffic: Vandenberg missile tests are a frequent and genuine source of “UFO” footage.',
      'Orbs over the deserts at dusk: satellites, and frequently Starlink trains.',
      'Formation lights over the Central Valley, usually traffic for the Bay Area and LA.',
      'Fireballs during major showers, reported by millions at once in such a big population.',
    ],
    incidents: [
      {
        year: 1942,
        name: 'The Battle of Los Angeles',
        body:
          'On the night of 25 February 1942, anti-aircraft batteries fired over 1,400 rounds at something above Los Angeles during the war-jittery weeks after Pearl Harbor. Nothing was hit and nothing was recovered; the object is usually attributed to a weather balloon, with the barrage itself to nerves.',
      },
      {
        year: 2004,
        name: 'The Nimitz Tic Tac',
        body:
          'Off San Diego in November 2004, F/A-18 crews from the USS Nimitz strike group tracked and chased a smooth white object the pilots said manoeuvred beyond known aircraft performance. The infrared video became public in 2017 and was officially released in 2020.',
        href: '/glossary/nimitz-incident',
      },
    ],
    faqs: [
      {
        question: 'What was the Battle of Los Angeles?',
        answer:
          'A wartime anti-aircraft barrage on 25 February 1942, hours after the Pearl Harbor attack, directed at an object or objects over Los Angeles. Around 1,400 rounds were fired. No wreckage was found, and the incident — immortalised by a famous newspaper photo — is usually attributed to a weather balloon and wartime panic.',
      },
      {
        question: 'Why are there so many UFO videos from California?',
        answer:
          'Vandenberg missile launches, carrier operations off San Diego, drones over the deserts and 40 million phone cameras. Rocket launches at dusk are the single most-filmed “UFO” event in the state, and they are genuinely spectacular.',
      },
    ],
  },
  // ---------------------------------------------------------------- Canada
  {
    slug: 'canada',
    name: 'Canada',
    region: 'North America',
    latitude: 56.13,
    longitude: -106.35,
    title: 'UFO Sightings in Canada',
    description:
      'Canadian UFO sightings: Shag Harbour, Falcon Lake, the Yukon object of 2022 and what explains most reports across the country.',
    intro: [
      'Canada’s reporting history is unusually well documented for a country its size, thanks to a military that investigated seriously through the 1960s and 70s and a national archive that kept the paperwork. Two 1967 cases — Shag Harbour in Nova Scotia and Falcon Lake in Manitoba — are among the best-evidenced anywhere.',
      'The vast majority of Canadian airspace goes unwatched, which is why the February 2022 object shot down over the Yukon drew so much attention: over such territory, most things pass unremarked.',
    ],
    reported: [
      'Northern lights misread as something structured — aurora is the most common source of “formation” reports from the territories.',
      'Satellite trains over the prairies, where dark-sky conditions are excellent.',
      'Orange lights drifting on lake country evenings: lanterns, and in one famous 1967 case, allegedly something else.',
      'Winter light phenomena: pillars, halos and dog prints, spectacular in prairie cold and regularly reported.',
    ],
    incidents: [
      {
        year: 1967,
        name: 'Shag Harbour, Nova Scotia',
        body:
          'Witnesses saw a lit object descend into the harbour on 4 October 1967; RCMP watched a glowing light beneath the surface, and Canadian military divers searched the seabed next day. No wreckage was found. It is one of the few official investigations of an object entering the water.',
        href: '/case/TU-1967-000001',
      },
      {
        year: 1967,
        name: 'Falcon Lake, Manitoba',
        body:
          'On 20 May 1967, prospector Stefan Michalak reported being burned by a landed craft at Falcon Lake, suffering grid-patterned burns and later illness. The site was investigated by the RCMP and health authorities; the case file is one of the few with physical medical evidence.',
      },
      {
        year: 2022,
        name: 'The Yukon object',
        body:
          'On 11 February 2022, a US F-22 shot down an unidentified high-altitude object over Canada’s Yukon Territory, at Ottawa’s request. Recovery efforts in the remote terrain were limited. The Canadian government’s own review of the incident has been reported in stages since.',
      },
    ],
    faqs: [
      {
        question: 'What happened at Falcon Lake?',
        answer:
          'In May 1967, prospector Stefan Michalak encountered a landed object in the Whiteshell area of Manitoba and was burned by what he described as its exhaust. Health records documented his injuries, the site was examined by the RCMP, and no conventional explanation has ever satisfied everyone involved.',
      },
      {
        question: 'Does Canada still investigate UFOs?',
        answer:
          'Not directly. Defence of North American airspace falls to NORAD, and unexplained tracks are now treated as potential national security matters — the reason the 2022 Yukon object was shot down. Civilian reporting goes to private databases.',
      },
    ],
  },
  // ---------------------------------------------------------------- Australia
  {
    slug: 'australia',
    name: 'Australia',
    region: 'Oceania',
    latitude: 25.27,
    longitude: 133.78,
    title: 'UFO Sightings in Australia',
    description:
      'Australian UFO sightings: the Westall school sighting of 1966, the HMAS Melbourne incident and what explains most reports across the country.',
    intro: [
      'Australia’s most famous UFO event belongs to its schoolchildren: in April 1966, roughly 200 pupils and teachers at Westall High School in Melbourne watched a silver object descend near the school oval and leave. No official Australian investigation followed, and six decades on the witnesses mostly still say what they saw.',
      'The country’s reports today follow its geography: bright satellites over the outback, lights near the RAAF’s ranges, and fireball meteors that cross half the continent in clear air.',
    ],
    reported: [
      'Satellite trains and ISS passes over the outback, where light pollution is effectively zero.',
      'Lights near Williamtown, Amberley and other RAAF bases, usually training traffic.',
      'Orange drifting lights over the coastal towns on summer evenings: lanterns at surf lifesaving events.',
      'The Tantanoola and southern coast reports, a steady trickle since the 1970s.',
    ],
    incidents: [
      {
        year: 1966,
        name: 'The Westall sighting',
        body:
          'On 6 April 1966, students and staff at Westall High School in suburban Melbourne watched a silver object descend, hover and depart at speed. No Australian government body investigated. It remains the country’s best-documented mass childhood sighting.',
        href: '/case/TU-1966-000001',
      },
      {
        year: 1965,
        name: 'The HMAS Melbourne incident',
        body:
          'During exercises in June 1965, the aircraft carrier and its escorts tracked an unidentified object that reportedly passed through the fleet at high speed. The ship’s log and a subsequent investigation exist in Australian files; interpretations of what the object was remain divided.',
      },
    ],
    faqs: [
      {
        question: 'Did anything land at Westall?',
        answer:
          'Witnesses reported the object descending near the school oval and something left behind — flattened grass, by some accounts. No physical evidence was preserved, and no official investigation was conducted. What makes the case notable is the number of independent witnesses, all children, giving consistent accounts.',
      },
      {
        question: 'Who do I report a UFO to in Australia?',
        answer:
          'There is no official Australian civilian channel. Reports go to private databases, and anything involving aircraft safety should be reported to Airservices Australia. This site takes Australian reports and checks them against satellite and astronomy data.',
      },
    ],
  },
  // ---------------------------------------------------------------- Zimbabwe
  {
    slug: 'zimbabwe',
    name: 'Zimbabwe',
    region: 'Southern Africa',
    latitude: 19.0,
    longitude: 29.8,
    title: 'UFO Sightings in Zimbabwe',
    description:
      'Zimbabwean UFO sightings: the Ariel School landing report of 1990 near Ruwa, investigated within days and still discussed three decades later.',
    intro: [
      'Zimbabwe’s place in the global UFO conversation rests on a single extraordinary morning. On 16 September 1990, during break at Ariel Primary School near Ruwa, dozens of children reported a silver craft and a figure near the school grounds. Astronomer Cynthia Hind was on the site within days, and psychiatrist John Mack interviewed the children later.',
      'What makes the Ariel School case unusual is not the claim but the witnesses: a large group of children who told consistent stories, drew the same pictures independently, and three decades later mostly still say the same thing.',
    ],
    reported: [
      'The Ariel School event remains the country’s defining case, and most Zimbabwean UFO discussion still circles it.',
      'Lights over the eastern highlands, where skies are dark and clear.',
      'Reports clustered around Ruwa and the Mashonaland East district — the Ariel School’s home ground.',
      'Occasional reports from safari guides and rural communities, rarely publicised internationally.',
    ],
    incidents: [
      {
        year: 1990,
        name: 'The Ariel School landing report',
        body:
          'Dozens of children aged roughly 6 to 12 reported a craft and a being near their school playground during morning break. Headmaster Colin Forbes took initial accounts; Cynthia Hind investigated within days; John Mack’s later interviews became the subject of a documentary and much debate.',
        href: '/case/TU-1994-000001',
      },
    ],
    faqs: [
      {
        question: 'What did the Ariel School children say they saw?',
        answer:
          'Most described a silver craft that landed or hovered near the playground, and a small figure. They drew similar pictures independently, and the accounts they gave on the day have largely held. Sceptical explanations point to suggestion, storytelling dynamics and an unusual natural object.',
      },
      {
        question: 'Is the Ariel School case considered credible?',
        answer:
          'It is one of the most-debated cases in the field. Investigators value it for the number of independent witnesses and the immediacy of the first accounts; sceptics note that the witnesses were young children interviewed without parents present. Neither side has convinced the other in thirty years.',
      },
    ],
  },
];

export const findPlace = (slug: string) => PLACES.find((p) => p.slug === slug);
